import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  getAccounts, getFiscalYears,
  getJournalEntry, createJournalEntry, updateJournalEntry, postJournalEntry,
} from "../../api/accountingApi";

const emptyLine = () => ({ account_id: null, description: "", debit: 0, credit: 0, _key: Date.now() + Math.random() });

const JournalEntryFormPage = () => {
  const toast = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [entry, setEntry] = useState(null);

  const [form, setForm] = useState({
    date: new Date(),
    description: "",
    reference: "",
    fiscal_year_id: null,
    period_id: null,
  });
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [accts, fys] = await Promise.all([
        getAccounts({ active: true }),
        getFiscalYears(),
      ]);

      // Filter to leaf accounts only (accounts with no children)
      const parentIds = new Set(accts.filter((a) => a.parent_id).map((a) => a.parent_id));
      const leafAccounts = accts.filter((a) => !parentIds.has(a.id));
      setAccounts(leafAccounts);
      setFiscalYears(fys);

      // Auto-select open fiscal year
      const openFy = fys.find((f) => f.status === "open");
      if (openFy) {
        setForm((prev) => ({ ...prev, fiscal_year_id: openFy.id }));
        setPeriods(openFy.periods || []);
        autoSelectPeriod(openFy.periods || [], new Date());
      }

      // Load existing entry if editing
      if (id) {
        const existing = await getJournalEntry(id);
        setEntry(existing);
        setForm({
          date: new Date(existing.date),
          description: existing.description,
          reference: existing.reference || "",
          fiscal_year_id: existing.fiscal_year_id,
          period_id: existing.period_id,
        });
        setLines(
          (existing.lines || []).map((l) => ({
            account_id: l.account_id,
            description: l.description || "",
            debit: parseFloat(l.debit) || 0,
            credit: parseFloat(l.credit) || 0,
            _key: l.id || Date.now() + Math.random(),
          }))
        );
        // Set periods for the entry's fiscal year
        const entryFy = fys.find((f) => f.id === existing.fiscal_year_id);
        if (entryFy) setPeriods(entryFy.periods || []);
      }
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  };

  const autoSelectPeriod = (periodList, date) => {
    const d = date instanceof Date ? date : new Date(date);
    const match = periodList.find((p) => {
      return new Date(p.start_date) <= d && d <= new Date(p.end_date);
    });
    if (match) {
      setForm((prev) => ({ ...prev, period_id: match.id }));
    }
  };

  const handleFiscalYearChange = (fyId) => {
    const fy = fiscalYears.find((f) => f.id === fyId);
    const fyPeriods = fy?.periods || [];
    setPeriods(fyPeriods);
    setForm((prev) => ({ ...prev, fiscal_year_id: fyId, period_id: null }));
    autoSelectPeriod(fyPeriods, form.date);
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, date }));
    autoSelectPeriod(periods, date);
  };

  const updateLine = (index, field, value) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index) => {
    if (lines.length <= 2) {
      toast.current?.show({ severity: "warn", summary: "Warning", detail: "Minimum 2 lines required" });
      return;
    }
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = difference < 0.01;

  const handleSave = async (andPost = false) => {
    if (!form.date || !form.description || !form.fiscal_year_id || !form.period_id) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Date, description, fiscal year, and period are required" });
      return;
    }

    if (!isBalanced) {
      toast.current?.show({ severity: "error", summary: "Validation", detail: `Debits and credits must be equal. Difference: ${difference.toFixed(2)}` });
      return;
    }

    const invalidLines = lines.filter((l) => !l.account_id || (l.debit === 0 && l.credit === 0));
    if (invalidLines.length > 0) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Each line must have an account and a debit or credit amount" });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        date: form.date.toISOString().split("T")[0],
        description: form.description,
        reference: form.reference || null,
        fiscal_year_id: form.fiscal_year_id,
        period_id: form.period_id,
        lines: lines.map((l) => ({
          account_id: l.account_id,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description || null,
        })),
      };

      let saved;
      if (isEdit) {
        saved = await updateJournalEntry(id, payload);
      } else {
        saved = await createJournalEntry(payload);
      }

      if (andPost && saved?.id) {
        await postJournalEntry(saved.id);
        toast.current?.show({ severity: "success", summary: "Success", detail: "Entry saved and posted" });
      } else {
        toast.current?.show({ severity: "success", summary: "Success", detail: "Entry saved as draft" });
      }

      navigate("/index/accounting/journal-entries");
    } catch (err) {
      toast.current?.show({
        severity: "error", summary: "Error",
        detail: err.response?.data?.message || "Failed to save entry",
      });
    } finally {
      setSaving(false);
    }
  };

  const accountOptions = accounts.map((a) => ({
    label: `${a.code} - ${a.name}`,
    value: a.id,
  }));

  const accountTemplate = (line, { rowIndex }) => {
    if (entry?.status && entry.status !== "draft") {
      const acct = accounts.find((a) => a.id === line.account_id);
      return acct ? `${acct.code} - ${acct.name}` : "";
    }
    return (
      <Dropdown
        value={line.account_id}
        options={accountOptions}
        onChange={(e) => updateLine(rowIndex, "account_id", e.value)}
        placeholder="Select Account"
        filter
        className="w-full p-inputtext-sm"
      />
    );
  };

  const debitTemplate = (line, { rowIndex }) => {
    if (entry?.status && entry.status !== "draft") {
      return parseFloat(line.debit).toLocaleString(undefined, { minimumFractionDigits: 2 });
    }
    return (
      <InputNumber
        value={line.debit || null}
        onValueChange={(e) => {
          updateLine(rowIndex, "debit", e.value || 0);
          if (e.value > 0) updateLine(rowIndex, "credit", 0);
        }}
        mode="decimal"
        minFractionDigits={2}
        className="w-full p-inputtext-sm"
      />
    );
  };

  const creditTemplate = (line, { rowIndex }) => {
    if (entry?.status && entry.status !== "draft") {
      return parseFloat(line.credit).toLocaleString(undefined, { minimumFractionDigits: 2 });
    }
    return (
      <InputNumber
        value={line.credit || null}
        onValueChange={(e) => {
          updateLine(rowIndex, "credit", e.value || 0);
          if (e.value > 0) updateLine(rowIndex, "debit", 0);
        }}
        mode="decimal"
        minFractionDigits={2}
        className="w-full p-inputtext-sm"
      />
    );
  };

  const lineDescTemplate = (line, { rowIndex }) => {
    if (entry?.status && entry.status !== "draft") return line.description;
    return (
      <InputText
        value={line.description}
        onChange={(e) => updateLine(rowIndex, "description", e.target.value)}
        className="w-full p-inputtext-sm"
        placeholder="Line memo"
      />
    );
  };

  const lineActionsTemplate = (line, { rowIndex }) => {
    if (entry?.status && entry.status !== "draft") return null;
    return (
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text p-button-danger p-button-sm"
        onClick={() => removeLine(rowIndex)}
      />
    );
  };

  const isDraft = !entry || entry.status === "draft";
  const periodOptions = periods
    .filter((p) => p.status === "open")
    .map((p) => ({ label: p.name, value: p.id }));

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex align-items-center gap-2 mb-3">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text p-button-sm"
          onClick={() => navigate("/index/accounting/journal-entries")}
        />
        <div>
          <h2 className="m-0">
            {isEdit ? `Journal Entry ${entry?.entry_number || ""}` : "New Journal Entry"}
          </h2>
          {entry?.status && (
            <Tag
              value={entry.status}
              severity={{ draft: "warning", posted: "success", void: "danger" }[entry.status]}
              className="mt-1"
            />
          )}
        </div>
      </div>

      {/* Header Form */}
      <div className="card p-3 mb-3">
        <div className="grid">
          <div className="col-12 md:col-3">
            <label className="block mb-1">Date *</label>
            <Calendar
              value={form.date}
              onChange={(e) => handleDateChange(e.value)}
              dateFormat="yy-mm-dd"
              showIcon
              disabled={!isDraft}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-3">
            <label className="block mb-1">Fiscal Year *</label>
            <Dropdown
              value={form.fiscal_year_id}
              options={fiscalYears.map((f) => ({ label: f.name, value: f.id }))}
              onChange={(e) => handleFiscalYearChange(e.value)}
              placeholder="Select Fiscal Year"
              disabled={!isDraft}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-3">
            <label className="block mb-1">Period *</label>
            <Dropdown
              value={form.period_id}
              options={periodOptions}
              onChange={(e) => setForm({ ...form, period_id: e.value })}
              placeholder="Select Period"
              disabled={!isDraft}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-3">
            <label className="block mb-1">Reference</label>
            <InputText
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Invoice #, Receipt #"
              disabled={!isDraft}
              className="w-full"
            />
          </div>
          <div className="col-12">
            <label className="block mb-1">Description *</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              disabled={!isDraft}
              className="w-full"
              placeholder="Describe this journal entry..."
            />
          </div>
        </div>
      </div>

      {/* Lines Table */}
      <div className="card p-3 mb-3">
        <div className="flex justify-content-between align-items-center mb-2">
          <h3 className="m-0">Entry Lines</h3>
          {isDraft && (
            <Button label="Add Line" icon="pi pi-plus" className="p-button-sm p-button-outlined" onClick={addLine} />
          )}
        </div>

        <DataTable value={lines} emptyMessage="No lines" className="p-datatable-sm">
          <Column header="Account *" body={accountTemplate} style={{ minWidth: "14rem" }} />
          <Column header="Description" body={lineDescTemplate} style={{ minWidth: "10rem" }} />
          <Column header="Debit" body={debitTemplate} style={{ minWidth: "8rem", textAlign: "right" }} />
          <Column header="Credit" body={creditTemplate} style={{ minWidth: "8rem", textAlign: "right" }} />
          {isDraft && <Column header="" body={lineActionsTemplate} style={{ width: "4rem" }} />}
        </DataTable>

        {/* Totals Bar */}
        <div
          className={`flex justify-content-end gap-4 p-3 mt-2 border-round ${isBalanced ? "bg-green-50" : "bg-red-50"}`}
        >
          <div>
            <span className="font-bold mr-2">Total Debit:</span>
            <span>{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div>
            <span className="font-bold mr-2">Total Credit:</span>
            <span>{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          {!isBalanced && (
            <div className="text-red-600 font-bold">
              Difference: {difference.toFixed(2)}
            </div>
          )}
          {isBalanced && totalDebit > 0 && (
            <div className="text-green-600 font-bold">
              <i className="pi pi-check mr-1" />Balanced
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {isDraft && (
        <div className="flex gap-2">
          <Button
            label="Save as Draft"
            icon="pi pi-save"
            className="p-button-outlined"
            onClick={() => handleSave(false)}
            loading={saving}
          />
          <Button
            label="Save & Post"
            icon="pi pi-check"
            onClick={() => handleSave(true)}
            loading={saving}
            disabled={!isBalanced || totalDebit === 0}
          />
        </div>
      )}

      {entry?.status === "void" && entry.void_reason && (
        <div className="card p-3 mt-3 bg-red-50">
          <h4 className="m-0 mb-1 text-red-600">Void Reason</h4>
          <p className="m-0">{entry.void_reason}</p>
        </div>
      )}

      <Toast ref={toast} />
    </div>
  );
};

export default JournalEntryFormPage;
